/**
 * App Component
 * 
 * 主應用程式組件
 * 整合所有 Context 和路由
 */

import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SavedStationsProvider } from './contexts/SavedStationsContext';
import { DictionaryProvider } from './contexts/DictionaryContext';
import { NotificationPreferencesProvider } from './contexts/NotificationPreferencesContext';
import { performMigration } from './utils/storageMigration';
import { getItem, setItem } from './utils/localStorage';
import { Snackbar, Alert, Button } from '@mui/material';
import { Box, CircularProgress } from '@mui/material';
import ErrorBoundary from './components/Common/ErrorBoundary';
import lazyWithRetry from './utils/lazyWithRetry';
import appInfo from '../package.json';
// PWA 安裝提示不需要 lazy load，需要快速顯示
import PWAInstallPrompt from './components/PWA/PWAInstallPrompt';
import PWAInstallBanner from './components/PWA/PWAInstallBanner';
import LoadingProgress from './components/Common/LoadingProgress';
import NetworkStatusBanner from './components/Common/NetworkStatusBanner';
import { FORCE_DICT_CHECK_ON_NEXT_LOAD_KEY } from './constants/updateKeys';

// Lazy load components
const LRT = React.lazy(lazyWithRetry(() => import('./components/LRT/LRT'), 'LRT'));
const MTR = React.lazy(lazyWithRetry(() => import('./components/MTR/MTR'), 'MTR'));
// 可以選擇使用 Header (Drawer) 或 HeaderMenu (Menu)
const Header = React.lazy(lazyWithRetry(() => import('./components/Common/Header'), 'Header'));
const MobileBottomNav = React.lazy(lazyWithRetry(() => import('./components/Common/MobileBottomNav'), 'MobileBottomNav'));
// const Header = React.lazy(() => import('./components/Common/HeaderMenu')); // 替代方案：使用 Menu 而不是 Drawer
const SaveStations = React.lazy(lazyWithRetry(() => import('./components/Settings/SaveStations'), 'SaveStations'));
const Settings = React.lazy(lazyWithRetry(() => import('./components/Settings/Settings'), 'Settings'));
const MTRMap = React.lazy(lazyWithRetry(() => import('./components/MTR/MTRRouteMap'), 'MTRMap'));
const MTRStatus = React.lazy(lazyWithRetry(() => import('./components/MTR/MTRStatus'), 'MTRStatus'));
const MTRBus = React.lazy(lazyWithRetry(() => import('./components/MTRBus/MTRBus'), 'MTRBus'));
const ImmigrationStatus = React.lazy(lazyWithRetry(() => import('./components/Immigration/ImmigrationStatus'), 'ImmigrationStatus'));
const TrafficNews = React.lazy(lazyWithRetry(() => import('./components/TrafficNews/TrafficNews'), 'TrafficNews'));

// Loading component
const PageLoading = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

// Version management
const APP_VERSION_KEY = 'appVersion';
const currentVersion = appInfo.version;

// 路由追蹤組件（用於 Google Analytics）
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    // 追蹤頁面瀏覽到 Google Analytics 4
    // 參考：https://ithelp.ithome.com.tw/articles/10358894
    const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
    if (GA4_MEASUREMENT_ID) {
      // 使用 react-ga4 的 send 方法追蹤頁面瀏覽（推薦方式）
      // 參考：https://ithelp.ithome.com.tw/articles/10358894
      const trackPageView = async () => {
        try {
          // 優先使用 window.ReactGA（如果已初始化）
          if (typeof window !== 'undefined' && window.ReactGA) {
            // 使用 react-ga4 的 send 方法追蹤頁面瀏覽
            // 對於 SPA，需要手動指定頁面路徑以正確追蹤路由變化
            window.ReactGA.send({
              hitType: 'pageview',
              page: location.pathname + location.search,
              title: document.title,
            });
            return true;
          }
          
          // Fallback: 動態導入 react-ga4
          const module = await import('react-ga4');
          const ReactGA = module.default || module;
          
          // 檢查 ReactGA 是否正確加載
          if (!ReactGA || typeof ReactGA.send !== 'function') {
            throw new Error('ReactGA.send 不是函數');
          }
          
          ReactGA.send({
            hitType: 'pageview',
            page: location.pathname + location.search,
            title: document.title,
          });
          return true;
        } catch (err) {
          console.warn('Failed to track pageview with react-ga4:', err);
          // Fallback: 使用 window.gtag 直接發送
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'page_view', {
              page_path: location.pathname + location.search,
              page_title: document.title,
              page_location: window.location.href,
            });
            return true;
          }
          return false;
        }
      };

      // 立即嘗試追蹤
      trackPageView().then((success) => {
        if (!success) {
          // 如果第一次失敗，等待一下再試（最多等待 2 秒）
          let attempts = 0;
          const maxAttempts = 20;
          const checkInterval = setInterval(() => {
            attempts++;
            trackPageView().then((success) => {
              if (success || attempts >= maxAttempts) {
                clearInterval(checkInterval);
              }
            });
          }, 100);
          
          // 2 秒後清除 interval（安全措施）
          setTimeout(() => clearInterval(checkInterval), 2000);
        }
      });
    }

    // 追蹤頁面瀏覽到 PostHog
    const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY;
    if (POSTHOG_API_KEY) {
      import('posthog-js').then((posthog) => {
        if (posthog.__loaded) {
          posthog.capture('$pageview');
        }
      }).catch((err) => {
        console.warn('Failed to track pageview with PostHog:', err);
      });
    }
  }, [location]);

  return null;
}

function App() {
  const [showUpdateBanner, setShowUpdateBanner] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [waitingWorker, setWaitingWorker] = React.useState(null);
  const hasRefreshedAfterSWUpdate = React.useRef(false);
  const routeContentSx = {
    pt: { xs: '64px', sm: '72px' },
    pb: { xs: 'calc(84px + env(safe-area-inset-bottom))', md: 0 },
  };

  const markDictionaryForceCheck = React.useCallback(() => {
    try {
      localStorage.setItem(FORCE_DICT_CHECK_ON_NEXT_LOAD_KEY, '1');
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to set dictionary force-check flag:', error);
      }
    }
  }, []);

  const navigateToFreshHome = React.useCallback(() => {
    const basePathRaw = process.env.PUBLIC_URL || '/';
    const basePath = basePathRaw.endsWith('/') ? basePathRaw : `${basePathRaw}/`;
    const nextUrl = new URL(basePath, window.location.origin);
    nextUrl.searchParams.set('__sw_refresh', String(Date.now()));
    window.location.replace(nextUrl.toString());
  }, []);

  // 開發環境：添加測試更新對話框的功能（按 Ctrl+Shift+U）
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handleTestUpdate = (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'U') {
          e.preventDefault();
          console.log('Test update dialog triggered');
          setShowUpdateBanner(true);
        }
      };
      window.addEventListener('keydown', handleTestUpdate);
      return () => window.removeEventListener('keydown', handleTestUpdate);
    }
  }, []);

  // 執行 localStorage migration
  useEffect(() => {
    try {
      performMigration();
    } catch (error) {
      console.error('Migration error:', error);
    }
  }, []);

  // Service Worker 更新檢測
  useEffect(() => {
    const UPDATE_EVENTS = ['swUpdate', 'sw-update-available'];

    // 監聽自定義的更新事件（由 serviceWorkerRegistration 觸發）
    const handleUpdateAvailable = (event) => {
      console.log('New version detected via Service Worker', event.detail);
      if (event.detail && event.detail.waiting) {
        setWaitingWorker(event.detail.waiting);
      }
      setShowUpdateBanner(true);
    };

    // 監聽 Service Worker 的更新事件
    if ('serviceWorker' in navigator) {
      // 監聽 controllerchange 事件（Service Worker 已更新）
      const handleControllerChange = () => {
        if (hasRefreshedAfterSWUpdate.current) return;
        hasRefreshedAfterSWUpdate.current = true;
        console.log('Service Worker controller changed, reloading...');
        // Service Worker 已更新後，強制回首頁重載，避免子路由更新競態造成 404
        navigateToFreshHome();
      };
      
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

      // 監聽自定義更新事件（新舊名稱都支援）
      UPDATE_EVENTS.forEach((eventName) => {
        window.addEventListener(eventName, handleUpdateAvailable);
      });

      // 檢查是否有等待中的 Service Worker（頁面加載時）
      // 這在 PWA standalone 模式下特別重要，因為應用可能從主屏幕啟動
      navigator.serviceWorker.ready.then((registration) => {
        console.log('Service Worker ready, checking for updates...', {
          waiting: registration.waiting,
          installing: registration.installing,
          controller: navigator.serviceWorker.controller,
          scope: registration.scope,
          // PWA 模式檢測
          isStandalone: window.matchMedia('(display-mode: standalone)').matches || 
                       (window.navigator.standalone === true) ||
                       document.referrer.includes('android-app://')
        });
        
        if (registration.waiting) {
          console.log('Found waiting Service Worker');
          setWaitingWorker(registration.waiting);
          setShowUpdateBanner(true);
        }
      }).catch((error) => {
        console.error('Error checking Service Worker status:', error);
      });

      // 定期檢查更新（每5分鐘檢查一次，用於開發測試）
      const checkInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }, 5 * 60 * 1000); // 5分鐘

      return () => {
        UPDATE_EVENTS.forEach((eventName) => {
          window.removeEventListener(eventName, handleUpdateAvailable);
        });
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
        clearInterval(checkInterval);
      };
    }
    UPDATE_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUpdateAvailable);
    });

    return () => {
      UPDATE_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleUpdateAvailable);
      });
    };
  }, [navigateToFreshHome]);

  // 處理更新確認
  const handleUpdateConfirm = () => {
    setIsUpdating(true);
    // App 更新後，下一次啟動時強制檢查字典版本
    markDictionaryForceCheck();
    
    if (waitingWorker) {
      // 告訴 Service Worker 跳過等待，立即激活新版本
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    } else {
      // 如果沒有等待中的 worker，直接重新加載
      setTimeout(() => {
        navigateToFreshHome();
      }, 1000);
    }
  };

  // 版本檢查（用於首次安裝或版本號變更）
  useEffect(() => {
    const storedVersion = getItem(APP_VERSION_KEY);
    
    if (!storedVersion) {
      setItem(APP_VERSION_KEY, currentVersion);
    } else if (storedVersion !== currentVersion) {
      // 應用版本有變更時，下一次啟動強制檢查字典版本
      markDictionaryForceCheck();
      // 版本號變更時更新本地存儲
      setItem(APP_VERSION_KEY, currentVersion);
    }
  }, [markDictionaryForceCheck]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <DictionaryProvider>
            <NotificationPreferencesProvider>
              <SavedStationsProvider>
              <LoadingProgress />
              <Suspense fallback={<PageLoading />}>
              <div className="App">
                <meta
                  name="viewport"
                  content="width=device-width, maximum-scale=1.0, user-scalable=0"
                />

                <Snackbar
                  open={showUpdateBanner}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  sx={{
                    mt: { xs: 7, sm: 8 },
                    width: { xs: 'calc(100% - 16px)', sm: 'auto' },
                  }}
                >
                  <Alert
                    severity="info"
                    variant="filled"
                    sx={{
                      width: '100%',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      '& .MuiAlert-message': {
                        width: '100%',
                        mr: 0,
                        mb: { xs: 1, sm: 0 },
                      },
                      '& .MuiAlert-action': {
                        width: { xs: '100%', sm: 'auto' },
                        ml: { xs: 0, sm: 1 },
                        pl: 0,
                        mr: 0,
                        mt: 0,
                        justifyContent: 'stretch',
                        alignItems: 'center',
                      },
                    }}
                    action={
                      isUpdating ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={18} color="inherit" />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr 1fr', sm: 'auto auto' },
                            gap: 1,
                            width: { xs: '100%', sm: 'auto' },
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleUpdateConfirm}
                            fullWidth
                            sx={{
                              bgcolor: 'common.white',
                              color: 'primary.main',
                              fontWeight: 700,
                              '&:hover': {
                                bgcolor: 'grey.100',
                              },
                            }}
                          >
                            更新
                          </Button>
                          <Button
                            color="inherit"
                            size="small"
                            fullWidth
                            variant="outlined"
                            onClick={() => setShowUpdateBanner(false)}
                            sx={{
                              borderColor: 'rgba(255,255,255,0.5)',
                              color: 'common.white',
                            }}
                          >
                            稍後
                          </Button>
                        </Box>
                      )
                    }
                  >
                    {isUpdating
                      ? '正在更新中，請稍候... Updating, please wait...'
                      : '偵測到新版本可用。New version available.'}
                  </Alert>
                </Snackbar>

                <BrowserRouter>
                  <RouteTracker />
                  <PWAInstallBanner />
                  <Header />
                  <NetworkStatusBanner />
                  <Routes>
                    <Route 
                      path="/lrt" 
                      element={
                        <Box sx={routeContentSx}>
                          <LRT />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/mtr/:link_Line/:link_Station" 
                      element={
                        <Box sx={routeContentSx}>
                          <MTR />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/mtrbus" 
                      element={
                        <Box sx={routeContentSx}>
                          <MTRBus />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/mRoute/:line" 
                      element={
                        <Box sx={routeContentSx}>
                          <MTRMap />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/settings" 
                      element={
                        <Box sx={routeContentSx}>
                          <Settings />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/mtr-status" 
                      element={
                        <Box sx={routeContentSx}>
                          <MTRStatus />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/immigration" 
                      element={
                        <Box sx={routeContentSx}>
                          <ImmigrationStatus />
                        </Box>
                      } 
                    />
                    <Route 
                      path="/traffic-news" 
                      element={
                        <Box sx={routeContentSx}>
                          <TrafficNews />
                        </Box>
                      } 
                    />
                    <Route
                      path="/"
                      element={
                        <Box sx={routeContentSx}>
                          <SaveStations />
                        </Box>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Box sx={routeContentSx}>
                          <SaveStations />
                        </Box>
                      }
                    />
                  </Routes>
                  <MobileBottomNav />
                </BrowserRouter>
                
                <Toaster
                  position="top-center"
                  toastOptions={{
                    duration: 3000,
                    style: {
                      background: '#333',
                      color: '#fff',
                    },
                  }}
                />
                
                <PWAInstallPrompt />
              </div>
              </Suspense>
              </SavedStationsProvider>
            </NotificationPreferencesProvider>
          </DictionaryProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
